import { areStringArraysEqual } from "@/common/helpers/are-string-arrays-equal";
import i18next from "i18next";
import { create, useStore } from "zustand";
import { devtools, persist } from "zustand/middleware";

export const enum Theme {
  Dark = "dark",
  Light = "light",
  System = "system",
}

export enum BaseScheme {
  Default = "default",
  TrueDark = "true-dark",
  TrueDarkDimmed = "true-dark-dimmed",
  TrueWhite = "true-white",
}

export enum AccentScheme {
  Default = "default",
  Classic = "classic",
  Ocean = "ocean",
  Amber = "amber",
  Rose = "rose",
  Violet = "violet",
}

export enum DateFormat {
  EuropeanDot = "DD.MM.YYYY",
  EuropeanSlash = "DD/MM/YYYY",
  US = "MM/DD/YYYY",
  ISO = "YYYY-MM-DD",
}

export enum NumberFormatPreference {
  FollowLanguage = "follow-language",
  German = "de-DE",
  EnglishUS = "en-US",
}

export enum Language {
  English = "en",
  German = "de",
}

export type SidebarCollapseState = Record<string, boolean>;

export function getDefaultDateFormat(): DateFormat {
  const locale = navigator.language || "de-DE";
  return locale === "en-US" ? DateFormat.US : DateFormat.EuropeanDot;
}

export const USER_SETTINGS_DEFAULTS = {
  theme: Theme.System,
  lightBaseScheme: BaseScheme.Default,
  darkBaseScheme: BaseScheme.Default,
  lightAccentScheme: AccentScheme.Classic,
  darkAccentScheme: AccentScheme.Classic,
  dateFormat: getDefaultDateFormat(),
  numberFormatPreference: NumberFormatPreference.FollowLanguage,
  language: Language.German,
  phonebookFiltersVisible: false,
  appOrder: [] as string[],
  dashboardHiddenAppIds: [] as string[],
};

export type DefaultState = {
  sidebarCollapseState: SidebarCollapseState;
  setSidebarSectionOpen: (sectionId: string, isOpen: boolean) => void;
  getSidebarSectionOpen: (sectionId: string, defaultValue: boolean) => boolean;
  showTanstackQueryButton: boolean;
  setShowTanstackQueryButton: (value: boolean) => void;
  resetUserSettings: () => void;
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  lightBaseScheme: BaseScheme;
  setLightBaseScheme: (scheme: BaseScheme) => void;
  darkBaseScheme: BaseScheme;
  setDarkBaseScheme: (scheme: BaseScheme) => void;
  lightAccentScheme: AccentScheme;
  setLightAccentScheme: (scheme: AccentScheme) => void;
  darkAccentScheme: AccentScheme;
  setDarkAccentScheme: (scheme: AccentScheme) => void;
  dateFormat: DateFormat;
  setDateFormat: (format: DateFormat) => void;
  numberFormatPreference: NumberFormatPreference;
  setNumberFormatPreference: (preference: NumberFormatPreference) => void;
  language: Language;
  setLanguage: (lang: Language) => void;
  changeLanguage: () => void;
  phonebookFiltersVisible: boolean;
  setPhonebookFiltersVisible: (visible: boolean) => void;
  appOrder: string[];
  setAppOrder: (ids: string[]) => void;
  dashboardHiddenAppIds: string[];
  setDashboardHiddenAppIds: (ids: string[]) => void;
};

export const userConfigStore = create<DefaultState>()(
  devtools(
    persist(
      (set, get) => ({
        sidebarCollapseState: {},
        setSidebarSectionOpen: (sectionId: string, isOpen: boolean) => {
          set((state) => ({
            sidebarCollapseState: {
              ...state.sidebarCollapseState,
              [sectionId]: isOpen,
            },
          }));
        },
        getSidebarSectionOpen: (sectionId: string, defaultValue: boolean) => {
          const state = get().sidebarCollapseState;
          return state[sectionId] ?? defaultValue;
        },
        showTanstackQueryButton: false,
        setShowTanstackQueryButton: (value: boolean) => {
          set({ showTanstackQueryButton: value });
        },
        resetUserSettings: () => {
          i18next.changeLanguage(USER_SETTINGS_DEFAULTS.language);
          set({ ...USER_SETTINGS_DEFAULTS });
        },
        theme: Theme.System,
        setTheme: (theme: Theme) => {
          set({ theme });
        },
        toggleTheme: () => {
          const newTheme = get().theme === Theme.Dark ? Theme.Light : Theme.Dark;
          set({ theme: newTheme });
        },
        lightBaseScheme: BaseScheme.Default,
        setLightBaseScheme: (scheme: BaseScheme) => {
          set({ lightBaseScheme: scheme });
        },
        darkBaseScheme: BaseScheme.Default,
        setDarkBaseScheme: (scheme: BaseScheme) => {
          set({ darkBaseScheme: scheme });
        },
        lightAccentScheme: AccentScheme.Classic,
        setLightAccentScheme: (scheme: AccentScheme) => {
          set({ lightAccentScheme: scheme });
        },
        darkAccentScheme: AccentScheme.Classic,
        setDarkAccentScheme: (scheme: AccentScheme) => {
          set({ darkAccentScheme: scheme });
        },
        dateFormat: getDefaultDateFormat(),
        setDateFormat: (format: DateFormat) => {
          set({ dateFormat: format });
        },
        numberFormatPreference: NumberFormatPreference.FollowLanguage,
        setNumberFormatPreference: (preference: NumberFormatPreference) => {
          set({ numberFormatPreference: preference });
        },
        language: Language.German,
        setLanguage: (lang: Language) => {
          i18next.changeLanguage(lang);
          set({ language: lang });
        },
        changeLanguage: () => {
          const newLanguage = get().language === Language.English ? Language.German : Language.English;
          i18next.changeLanguage(newLanguage);
          set({ language: newLanguage });
        },
        phonebookFiltersVisible: false,
        setPhonebookFiltersVisible: (visible: boolean) => {
          set({ phonebookFiltersVisible: visible });
        },
        appOrder: [],
        setAppOrder: (ids: string[]) => {
          set({ appOrder: ids });
        },
        dashboardHiddenAppIds: [],
        setDashboardHiddenAppIds: (ids: string[]) => {
          set({ dashboardHiddenAppIds: ids });
        },
      }),
      {
        name: "user-config-store",
        version: 1,
        migrate: (persistedState: unknown, version: number) => {
          const state = persistedState as Record<string, unknown>;

          if (version < 1) {
            if (state.lightAccentScheme === AccentScheme.Default) {
              state.lightAccentScheme = AccentScheme.Classic;
            }
            if (state.darkAccentScheme === AccentScheme.Default) {
              state.darkAccentScheme = AccentScheme.Classic;
            }
          }

          return state as DefaultState;
        },
      },
    ),
    { name: "user-config-store" },
  ),
);

export function useIsUserSettingsDefault(): boolean {
  return useStore(
    userConfigStore,
    (state) =>
      state.theme === USER_SETTINGS_DEFAULTS.theme &&
      state.lightBaseScheme === USER_SETTINGS_DEFAULTS.lightBaseScheme &&
      state.darkBaseScheme === USER_SETTINGS_DEFAULTS.darkBaseScheme &&
      state.lightAccentScheme === USER_SETTINGS_DEFAULTS.lightAccentScheme &&
      state.darkAccentScheme === USER_SETTINGS_DEFAULTS.darkAccentScheme &&
      state.dateFormat === USER_SETTINGS_DEFAULTS.dateFormat &&
      state.numberFormatPreference === USER_SETTINGS_DEFAULTS.numberFormatPreference &&
      state.language === USER_SETTINGS_DEFAULTS.language &&
      state.phonebookFiltersVisible === USER_SETTINGS_DEFAULTS.phonebookFiltersVisible &&
      areStringArraysEqual(state.appOrder, USER_SETTINGS_DEFAULTS.appOrder) &&
      areStringArraysEqual(state.dashboardHiddenAppIds, USER_SETTINGS_DEFAULTS.dashboardHiddenAppIds),
  );
}
